import { useEffect, useState } from "react"

import {
	Button,
	Dialog,
	RadioGroup,
	RadioGroupItem,
	Select,
} from "@productlane/ui"

export type AdminPlan = "starter" | "pro" | "scale"
export type AdminPlanInterval = "monthly" | "yearly"

export const ADMIN_PLAN_LABELS: Record<AdminPlan, string> = {
	starter: "Starter",
	pro: "Pro",
	scale: "Scale",
}

const PLAN_OPTIONS = (Object.keys(ADMIN_PLAN_LABELS) as AdminPlan[]).map(
	(value) => ({ value, label: ADMIN_PLAN_LABELS[value] }),
)

const INTERVAL_OPTIONS: Array<{ value: AdminPlanInterval; label: string }> = [
	{ value: "monthly", label: "Monthly" },
	{ value: "yearly", label: "Yearly" },
]

type CancelTiming = "period-end" | "now"

interface DialogProps<T> {
	isOpen: boolean
	onOpenChange: (open: boolean) => void
	isLoading: boolean
	onConfirm: (input: T) => void
}

export function CancelSubscriptionDialog({
	isOpen,
	onOpenChange,
	isLoading,
	onConfirm,
}: DialogProps<{ immediately: boolean }>) {
	const [timing, setTiming] = useState<CancelTiming>("period-end")
	const immediately = timing === "now"

	useEffect(() => {
		if (isOpen) setTiming("period-end")
	}, [isOpen])

	return (
		<Dialog
			isOpen={isOpen}
			onOpenChange={onOpenChange}
			title="Cancel subscription"
			description="Choose when the workspace loses its paid plan."
			footer={
				<>
					<Button
						variant="outline"
						onClick={() => {
							onOpenChange(false)
						}}
					>
						Keep subscription
					</Button>
					<Button
						variant={immediately ? "danger" : "default"}
						isLoading={isLoading}
						onClick={() => {
							onConfirm({ immediately })
						}}
					>
						Cancel subscription
					</Button>
				</>
			}
		>
			<RadioGroup
				className="px-1 py-2"
				value={timing}
				onValueChange={(value) => {
					setTiming(value as CancelTiming)
				}}
			>
				<RadioGroupItem
					value="period-end"
					label="At period end"
					description="The workspace keeps its plan until the current period or trial ends."
				/>
				<RadioGroupItem
					value="now"
					label="Immediately"
					description="The workspace moves to the free plan now. Stripe issues no refund."
				/>
			</RadioGroup>
		</Dialog>
	)
}

export function ChangePlanDialog({
	isOpen,
	onOpenChange,
	isLoading,
	onConfirm,
}: DialogProps<{ plan: AdminPlan; interval: AdminPlanInterval }>) {
	const [plan, setPlan] = useState<AdminPlan>("scale")
	const [interval, setBillingInterval] = useState<AdminPlanInterval>("monthly")

	useEffect(() => {
		if (!isOpen) return
		setPlan("scale")
		setBillingInterval("monthly")
	}, [isOpen])

	return (
		<Dialog
			isOpen={isOpen}
			onOpenChange={onOpenChange}
			title="Change plan"
			description="Stripe bills the price difference on the next invoice."
			footer={
				<>
					<Button
						variant="outline"
						onClick={() => {
							onOpenChange(false)
						}}
					>
						Cancel
					</Button>
					<Button
						isLoading={isLoading}
						onClick={() => {
							onConfirm({ plan, interval })
						}}
					>
						Change plan
					</Button>
				</>
			}
		>
			<div className="flex gap-3 px-1 py-2">
				<Select
					className="flex-1"
					name="admin-plan"
					label="Plan"
					value={plan}
					options={PLAN_OPTIONS}
					onChange={(value) => {
						setPlan(value as AdminPlan)
					}}
				/>
				<Select
					className="flex-1"
					name="admin-plan-interval"
					label="Billing"
					value={interval}
					options={INTERVAL_OPTIONS}
					onChange={(value) => {
						setBillingInterval(value as AdminPlanInterval)
					}}
				/>
			</div>
		</Dialog>
	)
}
